import { type InjectionKey, reactive, ref, watch } from "vue";
import {
  AnimationDirection,
  AnimationState,
  AveResponseEvents,
  MovementMode
} from "@org-avp/ave-message-factory";
import { getStaticImageAssetsOfEnvironments } from "./environments";
import type { AppStore } from "./app";
import { getIntersectionCaseInsensitive } from "@/utils/arrays";
import type { Logger } from "@/stores/logger";
import { getStaticImageAssetsOfAnimations } from "@/stores/animations";
import type { StaticImageAsset } from "@/utils/static-assets";
import type { ConnectionStore } from "@/stores/connection";
import { detectIsMobile, detectUserBrowser } from "@/utils/browser";
import type { ConfigStore } from "@/stores/config";
import type { States } from "@org-avp/ave-message-factory/src/model/state/ave-state";

export type JetStreamStore = ReturnType<typeof useJetstreamStore>;
export const JETSTREAM: InjectionKey<JetStreamStore> = Symbol("JETSTREAM");

export const useJetstreamStore = (
  appStore: AppStore,
  connectionStore: ConnectionStore,
  configStore: ConfigStore,
  log: Omit<Logger, "logId"> = console
) => {
  /**
   * Stream state
   */
  const streamInitialized = ref(false); // indicates the initial setup is done and the content is loaded
  const animationStaticImageAssets = ref<StaticImageAsset[]>([]);
  const environmentStaticImageAssets = ref<StaticImageAsset[]>([]);
  const activeEnvironment = ref<string>("AVP-deck-dummy-environment");
  const exteriorCameraIds = ref<string[]>([]);
  const interiorCameraIds = ref<string[]>([]);
  const activeCameraId = ref<string>("AVP-deck-dummy-camera");
  const activeView = ref<"interior" | "exterior">("exterior");

  // state in the AVE
  let aveAnimationIds: string[] = [];
  let aveCameraFlightsIds: string[] = [];
  let aveEnvironmentIds: string[] = [];
  let aveCameras: { cameraId: string; tags: string[] }[] = [];
  // use ref instead of reactive here, or it will not work with the reactive wrapper that gets returned from useJetstreamStore
  const aveAnimationStatePerAnimation = ref<Record<string, AnimationState>>({});
  const isCameraFlightPlaying = ref(false);
  const isAnimationSpotsEnabled = ref(false);

  watch(
    () => appStore.cameraHtmlAttribute,
    (newCameraId) => {
      if (
        newCameraId &&
        newCameraId.toLowerCase() != activeCameraId.value &&
        streamInitialized.value
      ) {
        changeCamera(newCameraId);
      }
    }
  );

  watch(
    () => appStore.cameraFlightHtmlAttribute,
    (newValue) => {
      // Note: if the html attribute changes two times very quickly, it might get out of sync with the ave
      // This happens if the attribute changes again before the AVE has completed loading the first change
      switch (newValue) {
        case "enabled":
          startCameraFlight();
          break;
        case "disabled":
          stopCameraFlight();
          break;
        default:
          log.warn(
            `${newValue} is not an expected value for the camera-flight attribute and will be ignored.`
          );
          return;
      }
    }
  );

  watch(
    () => appStore.hideConsumptionDataHtmlAttribute,
    async (newValue) => {
      await setConsumptionDataVisibility(!newValue);
    }
  );

  async function initializeStream() {
    const initialPrString = appStore.prStringHtmlAttribute;

    if (connectionStore.isNewSession) {
      try {
        await setupAveSessionAutomatically();
      } catch {
        log.warn("Failed starting AVE session, falling back to manual setup");
        await setupAveSessionManually();
      }
    }

    // no need to await this
    syncStateWithAve();
    // no need to await this
    setupEnvironments();
    // no need to await this
    setupAnimations();
    // no need to await this
    setupCameras();
    // no need to await this
    setupCameraFlights(true);

    if (configStore.config?.enableAnimationSpots) {
      // no need to await this
      setAnimationSpotsVisibility(true);
    }

    // need to set up cams before setting stream to initialized, to avoid issues when no new car is loaded (multi stream)
    // (watcher to change cam would be triggered be stream initialized being changed, but the cams would not be initialized yet)
    streamInitialized.value = true;
    appStore.emitEvent({ type: "streamInitialized", detail: true });
    connectionStore.jetStreamController?.onDisconnect(() => (streamInitialized.value = false));

    if (initialPrString !== appStore.prStringHtmlAttribute) {
      // we call selectCar for another time here in case the pr string changed while the stream was initializing
      await selectCar(appStore.prStringHtmlAttribute, appStore.consumptionDataHtmlAttribute);
    }
  }

  function prepareSessionClientData(): string {
    const clientData = {
      brand: configStore.config?.brand,
      service: configStore.config?.service,
      market: configStore.config?.market,
      mobile: detectIsMobile(),
      browser: detectUserBrowser()
    };

    return Object.entries(clientData)
      .map((entry) => `${entry[0]}:${entry[1]}`)
      .join(",");
  }

  async function setupAveSessionAutomatically() {
    const initialPrString = appStore.prStringHtmlAttribute;
    const initialConsumptionData = appStore.consumptionDataHtmlAttribute;
    const initialEnvironment = configStore.getInitialEnvironment();
    const initialCamera = configStore.getInitialCamera();

    await connectionStore.aveController?.startSession({
      loadingScreen: "Skip",
      sessionId: connectionStore.sessionId,
      client: prepareSessionClientData(),
      vehicle: initialPrString
        ? {
            prStringList: initialPrString.split(","),
            consumptionData: initialConsumptionData ? [initialConsumptionData] : undefined
          }
        : undefined,
      environment: initialEnvironment
        ? {
            environmentName: initialEnvironment
          }
        : undefined,
      camera: initialCamera
        ? {
            cameraId: initialCamera,
            transition: {
              movementMode: MovementMode.set
            }
          }
        : undefined,
      consumptionDataState: appStore.hideConsumptionDataHtmlAttribute ? "Disable" : "Enable"
    });
  }

  async function setupAveSessionManually() {
    try {
      await loadConfiguredEnvironment();
    } catch {
      log.warn("Failed loading configured environment, falling back to installed environment");
      // now setupEnvironments needs to load a verified environment
      await setupEnvironments(true);
    }

    if (appStore.prStringHtmlAttribute) {
      await selectCar(appStore.prStringHtmlAttribute, appStore.consumptionDataHtmlAttribute, true);
    }

    // no need to await this
    setConsumptionDataVisibility(!appStore.hideConsumptionDataHtmlAttribute);

    if (!isCameraFlightPlaying.value) {
      try {
        await loadConfiguredCamera();
      } catch {
        // just ignore error for unverified camera
      }
    }
  }

  async function loadConfiguredEnvironment(): Promise<void> {
    const configuredInitialEnvironment = configStore.config?.initialEnvironment;
    if (configuredInitialEnvironment) {
      return await changeEnvironment(configuredInitialEnvironment);
    }

    const configuredEnvironments = configStore.config?.environments;
    if (configuredEnvironments) {
      return await changeEnvironment(configuredEnvironments[0]);
    }

    const configuredDayEnvironment = configStore.config?.dayEnvironment;
    if (configuredDayEnvironment) {
      return await changeEnvironment(configuredDayEnvironment);
    }

    const configuredNightEnvironment = configStore.config?.nightEnvironment;
    if (configuredNightEnvironment) {
      return await changeEnvironment(configuredNightEnvironment);
    }
  }

  async function loadConfiguredCamera(): Promise<void> {
    if (appStore.cameraHtmlAttribute) {
      return await changeCamera(appStore.cameraHtmlAttribute, MovementMode.set);
    }

    const configuredInitialCamera = configStore.config?.initialCamera;
    if (configuredInitialCamera) {
      return await changeCamera(configuredInitialCamera, MovementMode.set);
    }

    const configuredInteriorCameras = configStore.config?.interiorCameras ?? [];
    const configuredExteriorCameras = configStore.config?.exteriorCameras ?? [];
    const allConfiguredCameras = [...configuredExteriorCameras, ...configuredInteriorCameras];
    if (allConfiguredCameras?.length) {
      return await changeCamera(allConfiguredCameras[0], MovementMode.set);
    }
  }

  async function selectCar(
    newPrString?: string,
    consumptionData?: string,
    skipPostLongLoadingSetup = false
  ): Promise<void> {
    if (!newPrString) {
      throw connectionStore.handleAndGenerateError(
        "CRITICAL",
        "PR string not provided to select car",
        "PR_STRING_NOT_PROVIDED"
      );
    }

    try {
      await connectionStore.aveController?.selectCar(
        newPrString.split(","),
        undefined,
        undefined,
        consumptionData?.split(",")
      );

      // During stream initialization if the automatic session setup fails after it already loaded the car,
      // it continues with the manual setup.
      // During this initialization we skip this additional setup here and call it explicitly afterward.
      if (!skipPostLongLoadingSetup) {
        setupAnimations();
        setupCameras();
        setupCameraFlights();
      }
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "CRITICAL",
        `Unable to select car.`,
        "AVE_CAR_NOT_INSTALLED",
        e as Error
      );
    }
  }

  async function setAnimationSpotsVisibility(isVisible: boolean): Promise<void> {
    try {
      await connectionStore.aveController?.setAnimationSpotsVisibility(isVisible);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to set animation spot visibility. Reason: ${e}`
      );
    }
  }

  async function changeEnvironment(environment: string): Promise<void> {
    try {
      await connectionStore.aveController?.changeEnvironment(environment);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to change environment to ${environment}. Reason: ${e}`
      );
    }
  }

  async function updateStateWithAveMessage(aveMessage: Record<string, any>) {
    if (aveMessage?.meta?.name === AveResponseEvents.AVEStartOrResumeCameraOrbit) {
      log.debug("Received started camera flight, syncing app state");
      isCameraFlightPlaying.value = true;
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVEStopCameraOrbit) {
      log.debug("Received stopped camera flight, syncing app state");
      isCameraFlightPlaying.value = false;
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVESetAnimationSpotsVisibility) {
      log.debug("Received changed animation spots visibility, syncing app state");
      // Currently the response of AVESetAnimationSpotsVisibility is not returning the new state,
      // so we need to fetch it separately
      connectionStore.aveController?.getAveState();
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVEAnimationUpdate) {
      log.debug("Received animation update, syncing app state");
      const animationId = aveMessage?.data?.animationId.toLowerCase();
      const animationState = aveMessage?.data?.state.toLowerCase();

      aveAnimationStatePerAnimation.value = {
        ...aveAnimationStatePerAnimation.value,
        [animationId]: animationState
      };

      if (animationId === "doorfl") {
        // Since "doors" is a composite animation, the AVE does not have a state for it,
        // we use "doorfl" to simulate the state of "doors"
        aveAnimationStatePerAnimation.value = {
          ...aveAnimationStatePerAnimation.value,
          ["doors"]: animationState
        };
      }
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVESelectCamera) {
      log.debug("Received camera update, syncing app state");
      handleNewCameraId(aveMessage?.data?.cameraId?.toLowerCase());
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVESelectEnvironment) {
      log.debug("Received environment update, syncing app state");
      activeEnvironment.value = aveMessage?.data?.environmentName?.toLowerCase();
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVESelectCar) {
      connectionStore.aveController?.getAveState();
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVEDisplayConsumptionData) {
      connectionStore.aveController?.getAveState();
    } else if (aveMessage?.meta?.name === AveResponseEvents.AVEGetStates) {
      activeEnvironment.value =
        aveMessage?.data?.environmentStates?.activeEnvironment?.toLowerCase();
      handleNewCameraId(aveMessage?.data?.cameraStates?.currentCamera?.toLowerCase());
      isCameraFlightPlaying.value = Boolean(
        aveMessage?.data?.cameraOrbitStates?.currentCameraOrbit
      );
      isAnimationSpotsEnabled.value =
        aveMessage?.data?.animationSpotStates?.animationSpotState?.toLowerCase() !== "disabled";

      updateConsumptionDataVisibilityState(aveMessage?.data);
      updatePrStringAndConsumptionDataState(aveMessage?.data);
    }
  }

  async function syncStateWithAve() {
    connectionStore.jetStreamController?.onDatachannelMessage(updateStateWithAveMessage);
    connectionStore.aveController?.getAveState();
  }

  /**
   * Get animations that are supported by AVE and Deck
   */
  async function setupAnimations(): Promise<void> {
    log.info("Setting up animations");
    aveAnimationIds = []; // reset cached animation IDs

    const installedAnimations = await getInstalledAnimationIds();
    animationStaticImageAssets.value = getStaticImageAssetsOfAnimations(
      installedAnimations,
      configStore.config?.brand! // TODO: catch potentially unset or invalid brand
    );

    log.debug("Using the following animations:", animationStaticImageAssets.value);
  }

  /**
   * Get camera flights that are supported by AVE and Deck
   */
  async function setupCameraFlights(startCameraFlightWhenReady = false): Promise<void> {
    log.info("Setting up camera flights");
    aveCameraFlightsIds = []; // reset cached camera flight IDs

    const installedCameraFlights = await getInstalledCameraFlightIds();
    const configuredCameraFlight = configStore.config?.cameraFlight;
    const verifiedCameraFlight = installedCameraFlights.find(
      (installedCameraFlight) =>
        installedCameraFlight.toLowerCase() === configuredCameraFlight?.toLowerCase()
    );
    configStore.verifiedConfig.cameraFlight = verifiedCameraFlight;

    log.debug("Using the following camera flight:", verifiedCameraFlight);

    if (startCameraFlightWhenReady) {
      if (
        (configStore.config?.cameraFlightOnStart &&
          appStore.cameraFlightHtmlAttribute !== "disabled") ||
        appStore.cameraFlightHtmlAttribute === "enabled"
      ) {
        await startCameraFlight();
      }
    }
  }

  /**
   * Get environments from configuration, but make sure they are supported by AVE and Deck
   */
  async function setupEnvironments(loadVerifiedEnvironmentWhenReady = false): Promise<void> {
    log.info("Setting up environments");
    aveEnvironmentIds = []; // reset cached environment IDs

    const configuredEnvironments = configStore.config?.environments;
    const configuredNightEnvironment = configStore.config?.nightEnvironment;
    const configuredDayEnvironment = configStore.config?.dayEnvironment;

    // use a separate variable for setting verifiedEnvironmentToLoad.value to not have multiple re-renders
    let environmentToLoad: string | undefined = undefined;

    if (configuredEnvironments) {
      // the user wants a gallery, so now we validate the environments
      const installedEnvironments = await filterInstalledEnvironments(configuredEnvironments);
      environmentStaticImageAssets.value =
        getStaticImageAssetsOfEnvironments(installedEnvironments);

      if (environmentStaticImageAssets.value.length) {
        log.debug(
          "Configured environments that are supported:",
          environmentStaticImageAssets.value
        );
        environmentToLoad = environmentStaticImageAssets.value[0].id;
      } else {
        log.warn(
          `None of the configured environments '${configuredEnvironments}' are supported by AVE or Deck.`
        );
      }
    } else if (configuredDayEnvironment && configuredNightEnvironment) {
      // the user wants a day/night toggle, so now we validate the environments
      const [installedDay, installedNight] = await filterInstalledEnvironments([
        configuredDayEnvironment,
        configuredNightEnvironment
      ]);

      environmentStaticImageAssets.value = getStaticImageAssetsOfEnvironments([
        installedDay,
        installedNight
      ]);

      if (installedNight) {
        environmentToLoad = installedNight;
      } else {
        log.warn(
          `Configured night environment '${configuredNightEnvironment}' not supported by AVE or Deck.`
        );
      }

      if (installedDay) {
        environmentToLoad = installedDay;
      } else {
        log.warn(
          `Configured day environment '${configuredDayEnvironment}' not supported by AVE or Deck.`
        );
      }
    }

    const configuredInitialEnvironment = configStore.config?.initialEnvironment;
    if (configuredInitialEnvironment) {
      const [installedInitial] = await filterInstalledEnvironments([configuredInitialEnvironment]);
      if (installedInitial) {
        environmentToLoad = installedInitial;
      } else {
        log.warn(
          `Configured initial environment '${configuredInitialEnvironment}' not supported by AVE.`
        );
      }
    }

    if (loadVerifiedEnvironmentWhenReady) {
      try {
        // in case none of the configured environments is supported, use the first installed environment
        await changeEnvironment(environmentToLoad ?? aveEnvironmentIds[0]);
      } catch (error) {
        throw connectionStore.handleAndGenerateError(
          "CRITICAL",
          `Unable to select environment.`,
          "AVE_ENVIRONMENT_NOT_INSTALLED",
          error as Error
        );
      }
    }
  }

  /**
   * Get cameras from configuration, but make sure they are supported by AVE
   */
  async function setupCameras(): Promise<void> {
    log.info("Setting up cameras");
    aveCameras = []; // reset cached cameras

    const configuredInteriorCameraIds = configStore.config?.interiorCameras ?? [];
    const configuredExteriorCameraIds = configStore.config?.exteriorCameras ?? [];

    interiorCameraIds.value = await filterInstalledCameras(configuredInteriorCameraIds);
    exteriorCameraIds.value = await filterInstalledCameras(configuredExteriorCameraIds);

    log.debug("Using the following interior cameras:", interiorCameraIds.value);
    log.debug("Using the following exterior cameras:", exteriorCameraIds.value);

    // check if configured cameras are actually available and log a warning if they aren't
    const allConfiguredCameraIds = [...exteriorCameraIds.value, ...interiorCameraIds.value];
    if (!allConfiguredCameraIds?.length) {
      log.warn(`None of the configured cameras '${allConfiguredCameraIds}' are supported by AVE.`);
    }

    const configuredInitialCameraId = configStore.config?.initialCamera;
    if (configuredInitialCameraId) {
      const [installedInitialCameraId] = await filterInstalledCameras([configuredInitialCameraId]);
      if (!installedInitialCameraId) {
        log.warn(`Configured initial camera '${configuredInitialCameraId}' not supported by AVE.`);
      }
    }

    if (appStore.cameraHtmlAttribute) {
      const [installedDynamicCameraId] = await filterInstalledCameras([
        appStore.cameraHtmlAttribute
      ]);
      if (!installedDynamicCameraId) {
        log.warn(
          `Dynamic camera '${appStore.cameraHtmlAttribute}' provided via HTML attribute not supported by AVE.`
        );
      }
    }
  }

  function handleNewCameraId(newCameraId: string): void {
    activeCameraId.value = newCameraId;

    appStore.emitAttributeChange("camera", newCameraId);

    // to determine if the new camera is an interior or exterior, we check the tags of the camera objects that we saved
    const newCamera = aveCameras.find((camera) => camera.cameraId.toLowerCase() === newCameraId);
    const newCameraTags = newCamera?.tags.map((tag) => tag.toLowerCase());
    const newActiveView = newCameraTags?.find((tag) => tag === "interior" || tag === "exterior");
    if (!newActiveView) {
      log.warn(
        `Could not identify active view for camera '${newCameraId}', defaulting to exterior view`
      );
      activeView.value = "exterior";
      return;
    }
    activeView.value = newActiveView as "interior" | "exterior";
  }

  function updatePrStringAndConsumptionDataState(aveState: States): void {
    if (aveState?.carStates?.consumptionData?.join(",") !== appStore.consumptionDataHtmlAttribute) {
      log.debug("Received car update, syncing consumption data");
      appStore.emitAttributeChange(
        "consumptionData",
        aveState?.carStates?.consumptionData?.join(",")
      );
    }
    if (aveState?.carStates?.currentPRCodes?.join(",") !== appStore.prStringHtmlAttribute) {
      log.debug("Received car update, syncing PR string");
      appStore.emitAttributeChange("prString", aveState?.carStates?.currentPRCodes?.join(","));
    }
  }

  function updateConsumptionDataVisibilityState(aveState: States): void {
    if (
      aveState?.carStates?.displayConsumptionData !== !appStore.hideConsumptionDataHtmlAttribute
    ) {
      log.debug("Received consumption data display update, syncing consumption data visibility");
      appStore.emitAttributeChange(
        "hideConsumptionData",
        !aveState?.carStates?.displayConsumptionData
      );
    }
  }

  async function filterInstalledEnvironments(environments: string[]): Promise<string[]> {
    const aveEnvironmentIds = await getInstalledEnvironmentsIds();
    return getIntersectionCaseInsensitive(environments, aveEnvironmentIds);
  }

  async function filterInstalledCameras(cameraIds: string[]): Promise<string[]> {
    const aveCameraIds = await getInstalledCameraIds();
    return getIntersectionCaseInsensitive(cameraIds, aveCameraIds);
  }

  async function changeCamera(
    cameraId: string,
    movementMode: MovementMode = MovementMode.flysmooth
  ): Promise<void> {
    try {
      await connectionStore.aveController?.changeCamera(cameraId, movementMode);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to change camera to ${cameraId}. Reason: ${e}`
      );
    }
  }

  async function zoom(distance: number) {
    try {
      await connectionStore.aveController?.moveCamera(0, 0, distance);
    } catch (e) {
      throw connectionStore.handleAndGenerateError("NON_CRITICAL", `Unable to zoom. Reason: ${e}`);
    }
  }

  async function startCameraFlight(): Promise<void> {
    if (!configStore.verifiedConfig?.cameraFlight) {
      log.warn("No valid camera flight available");
      return;
    }

    try {
      await connectionStore.aveController?.startOrResumeCameraOrbit(
        configStore.verifiedConfig?.cameraFlight
      );
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to start camera flight. Reason: ${e}`
      );
    }
  }

  async function stopCameraFlight(): Promise<void> {
    try {
      await connectionStore.aveController?.stopCameraOrbit();
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to stop camera flight. Reason: ${e}`
      );
    }
  }

  async function setConsumptionDataVisibility(isVisible: boolean): Promise<void> {
    try {
      await connectionStore.aveController?.displayConsumptionData(isVisible);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to set consumption data visibility. Reason: ${e}`
      );
    }
  }

  function getNextAnimationDirection(animation: string): AnimationDirection | undefined {
    switch (aveAnimationStatePerAnimation.value[animation].toLowerCase()) {
      case AnimationState.Initial.toLowerCase():
        return AnimationDirection.forward;
      case AnimationState.Animated.toLowerCase():
        return AnimationDirection.reverse;
      case AnimationState.Running.toLowerCase():
        log.debug("Not toggling animation while it is still running");
        return;
      default:
        log.warn("Unsupported animation state", aveAnimationStatePerAnimation.value[animation]);
        return;
    }
  }

  async function toggleAnimation(animation: string): Promise<void> {
    try {
      const nextAnimationDirection = getNextAnimationDirection(animation);
      if (!nextAnimationDirection) {
        log.warn(
          `No next animation direction for animation "${animation}", skipping toggling animation`
        );
        return;
      }

      if (animation === "doors") {
        // The "doors" animation is a composite of all individual doors.
        // If one of the individual doors is in the wrong state the whole "doors" animation will be ignored and no door will be animated.
        // To still animate the other doors, we trigger each of them separately instead of using the composite "doors"
        await Promise.all([
          aveAnimationIds.includes("doorfl") &&
            connectionStore.aveController?.triggerAnimation("doorfl", nextAnimationDirection),
          aveAnimationIds.includes("doorfr") &&
            connectionStore.aveController?.triggerAnimation("doorfr", nextAnimationDirection),
          aveAnimationIds.includes("doorrl") &&
            connectionStore.aveController?.triggerAnimation("doorrl", nextAnimationDirection),
          aveAnimationIds.includes("doorrr") &&
            connectionStore.aveController?.triggerAnimation("doorrr", nextAnimationDirection)
        ]);
        return;
      }

      await connectionStore.aveController?.triggerAnimation(animation, nextAnimationDirection);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to toggle animation ${animation}. Reason: ${e}`
      );
    }
  }

  async function getInstalledEnvironmentsIds(): Promise<string[]> {
    try {
      if (!aveEnvironmentIds?.length) {
        // fetch environments and save it to state
        const aveEnvironments = await connectionStore.aveController!.loadEnvironments();
        aveEnvironmentIds = aveEnvironments.map((env) => env.environmentId);
      }
      return aveEnvironmentIds;
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to get installed environments. Reason: ${e}`
      );
    }
  }

  async function getInstalledCameraIds(): Promise<string[]> {
    try {
      if (!aveCameras?.length) {
        // fetch cameras and save it to state
        aveCameras = await connectionStore.aveController!.loadCameras();
      }
      return aveCameras.map((camera) => camera.cameraId);
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable to get installed cameras. Reason: ${e}`
      );
    }
  }

  async function getInstalledAnimationIds(): Promise<string[]> {
    try {
      if (!aveAnimationIds?.length) {
        // fetch animations and save it to state
        const aveAnimations = await connectionStore.aveController!.loadAnimations();

        const animationStatePerAnimation: Record<string, AnimationState> = {};

        for (const animation of aveAnimations) {
          aveAnimationIds.push(animation.animationId.toLowerCase());
          animationStatePerAnimation[animation.animationId.toLowerCase()] =
            animation.state.toLowerCase() as AnimationState;
        }

        aveAnimationStatePerAnimation.value = animationStatePerAnimation;
      }
      return aveAnimationIds;
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable get installed animations. Reason: ${e}`
      );
    }
  }

  async function getInstalledCameraFlightIds(): Promise<string[]> {
    try {
      if (!aveCameraFlightsIds?.length) {
        // fetch camera flights and save it to state
        aveCameraFlightsIds = await connectionStore.aveController!.listCameraOrbits();
      }
      return aveCameraFlightsIds;
    } catch (e) {
      throw connectionStore.handleAndGenerateError(
        "NON_CRITICAL",
        `Unable get installed camera flights. Reason: ${e}`
      );
    }
  }

  return reactive({
    streamInitialized: streamInitialized,
    initializeStream: initializeStream,
    updateStateWithAveMessage: updateStateWithAveMessage,
    selectCar: selectCar,
    setAnimationSpotsVisibility: setAnimationSpotsVisibility,
    changeEnvironment: changeEnvironment,
    changeCamera: changeCamera,
    toggleAnimation: toggleAnimation,
    startCameraFlight: startCameraFlight,
    stopCameraFlight: stopCameraFlight,
    setConsumptionDataVisibility: setConsumptionDataVisibility,
    zoom: zoom,
    animationStaticImageAssets: animationStaticImageAssets,
    aveAnimationStatePerAnimation: aveAnimationStatePerAnimation,
    aveCameraFlightsIds: aveCameraFlightsIds,
    environmentStaticImageAssets: environmentStaticImageAssets,
    activeEnvironment: activeEnvironment,
    exteriorCameraIds: exteriorCameraIds,
    interiorCameraIds: interiorCameraIds,
    activeCameraId: activeCameraId,
    activeView: activeView,
    isCameraFlightPlaying: isCameraFlightPlaying,
    isAnimationSpotsEnabled: isAnimationSpotsEnabled
  });
};
